import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiFeil } from '../../api/klient';
import { FLAGGSTATUSER, konsollApi, type Flagg } from '../../api/konsoll';

/** Flaggene i reisen: status og notat kan endres. Flagg som er trukket av systemet, vises nederst uten skjema. */
export default function FlaggPanel({ reiseId, flagg }: { reiseId: string; flagg: Flagg[] }) {
  const { t } = useTranslation();
  const [liste, setListe] = useState(flagg);

  if (liste.length === 0) return <p className="text-slate-700">{t('konsoll.flagg.ingen')}</p>;

  const sortert = [...liste].sort((a, b) => Number(a.status === 'trukket') - Number(b.status === 'trukket'));
  return (
    <ul className="flex flex-col gap-3">
      {sortert.map((f) => (
        <li key={f.id}>
          <Flaggkort reiseId={reiseId} flagg={f} onLagret={(ny) => setListe((l) => l.map((x) => (x.id === ny.id ? ny : x)))} />
        </li>
      ))}
    </ul>
  );
}

function Flaggkort({ reiseId, flagg, onLagret }: { reiseId: string; flagg: Flagg; onLagret: (flagg: Flagg) => void }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState(flagg.status);
  const [notat, setNotat] = useState(flagg.notat ?? '');
  const [melding, setMelding] = useState<{ feil: boolean; tekst: string } | null>(null);
  const trukket = flagg.status === 'trukket';
  const id = `flagg-${flagg.id}`;

  async function lagre() {
    setMelding(null);
    try {
      const ny = await konsollApi.oppdaterFlagg(reiseId, flagg.id, { status, notat });
      onLagret(ny);
      setMelding({ feil: false, tekst: t('konsoll.flagg.lagret') });
    } catch (e) {
      setMelding({ feil: true, tekst: t(`feil.${e instanceof ApiFeil ? e.kode : 'ukjent'}`, { defaultValue: t('feil.ukjent') }) });
    }
  }

  return (
    <article aria-labelledby={`${id}-type`} className={'rounded-lg border bg-white p-4 ' + (trukket ? 'border-slate-200 opacity-70' : 'border-slate-300')}>
      <h3 id={`${id}-type`} className="font-semibold">
        {t(`reise.flaggtype.${flagg.type}`, { defaultValue: flagg.type })}
        <span className="ml-2 text-sm font-normal text-slate-600">{t(`konsoll.flaggstatus.${flagg.status}`, { defaultValue: flagg.status })}</span>
      </h3>
      <p className="mb-2 break-words text-slate-800">{flagg.tekst}</p>
      {flagg.endretAv && <p className="mb-2 text-xs text-slate-600">{t('konsoll.flagg.endret', { av: flagg.endretAv })}</p>}
      {!trukket && (
        <div className="grid gap-3 sm:grid-cols-[auto_1fr_auto] sm:items-end">
          <div className="flex flex-col">
            <label htmlFor={`${id}-status`} className="mb-1 text-sm font-medium">
              {t('konsoll.flagg.status')}
            </label>
            <select id={`${id}-status`} value={status} onChange={(e) => setStatus(e.target.value)} className="felt">
              {FLAGGSTATUSER.map((s) => (
                <option key={s} value={s}>
                  {t(`konsoll.flaggstatus.${s}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col">
            <label htmlFor={`${id}-notat`} className="mb-1 text-sm font-medium">
              {t('konsoll.flagg.notat')}
            </label>
            <input id={`${id}-notat`} value={notat} onChange={(e) => setNotat(e.target.value)} className="felt" />
          </div>
          <button type="button" onClick={lagre} className="knapp-sekundaer">
            {t('konsoll.flagg.lagre')}
          </button>
        </div>
      )}
      {melding && (
        <p role={melding.feil ? 'alert' : 'status'} className={'mt-2 text-sm ' + (melding.feil ? 'text-red-800' : 'text-aksent-800')}>
          {melding.tekst}
        </p>
      )}
    </article>
  );
}
